import { FormattedMessage } from 'react-intl';
import { Card, Empty, Skeleton } from 'antd';
import {
  FileTextOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { IDocumentListProps, IDocumentItem } from './types';

const formatDate = (dateInput: string | Date): string => {
  return dayjs(dateInput).format('DD/MM/YYYY HH:mm');
};

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return <FilePdfOutlined className={'text-red-500 text-lg shrink-0'} />;
    case 'doc':
    case 'docx':
      return <FileWordOutlined className={'text-blue-600 text-lg shrink-0'} />;
    case 'xls':
    case 'xlsx':
    case 'csv':
      return <FileExcelOutlined className={'text-green-600 text-lg shrink-0'} />;
    case 'jpg':
    case 'jpeg':
    case 'png':
      return <FileImageOutlined className={'text-purple-500 text-lg shrink-0'} />;
    default:
      return <FileTextOutlined className={'text-blue-500 text-lg shrink-0'} />;
  }
};

const sortByCreatedAt = (a: IDocumentItem, b: IDocumentItem) => {
  const dateA = new Date(a.createdAt);
  const dateB = new Date(b.createdAt);
  return dateB.getTime() - dateA.getTime();
};

const prettifyEnum = (value: string): string => {
  return value
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const DocumentList = ({ documents, isLoading, onDocumentClick, title }: IDocumentListProps) => {
  const sortedDocuments = [...documents].sort(sortByCreatedAt);

  return (
    <Card
      className={'shadow-sm'}
      styles={{
        body: {
          padding: '16px',
        },
      }}
    >
      <div className={'flex items-center gap-2 mb-4'}>
        <FileTextOutlined />
        <h5 className={'m-0'}>{title || <FormattedMessage id={'applicationDetails.uploadedDocuments.title'} />}</h5>
        {documents.length > 0 && <span className={'text-xs text-gray-500'}>({documents.length})</span>}
      </div>
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : sortedDocuments.length > 0 ? (
        <div className={'space-y-3 pr-1'}>
          {sortedDocuments.map(document => (
            <div
              key={document.id}
              onClick={() => onDocumentClick(document)}
              className={
                'flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 transition-all hover:border-green-300 hover:bg-green-50 cursor-pointer'
              }
            >
              <div className={'flex items-center gap-3 flex-1 min-w-0'}>
                {getFileIcon(document.originalFileName || '')}
                <div className={'flex-1 min-w-0'}>
                  <p className={'m-0 text-sm font-medium text-gray-900 truncate'}>{document.originalFileName}</p>
                  <div className={'flex items-center gap-2 mt-1'}>
                    <span className={'text-xs text-gray-500'}>{formatDate(document.createdAt)}</span>
                    {document.type && (
                      <>
                        <span className={'text-xs text-gray-400'}>•</span>
                        <span className={'text-xs text-green-600 font-medium'}>{prettifyEnum(document.type)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className={'py-8'} />
      )}
    </Card>
  );
};
